import { useState, useEffect, useRef } from "react";
import { searchEscoSkills } from "../../../services/escoSkillsService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faSpinner } from "@fortawesome/free-solid-svg-icons";

const SkillAutocomplete = ({ 
  value, 
  onChange, 
  onSelect, 
  onKeyDown,
  isSelected = false,
  placeholder = "Add skill...",
  className = "",
  type = "hard-skill", // "hard-skill" or "soft-skill"
  minQueryLength = 2,
  maxSuggestions = 10 
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const inputRef = useRef();
  const suggestionsRef = useRef();
  const searchTimeoutRef = useRef();

  // Debounced search function
  const searchSkills = async (query) => {
    if (query.length < minQueryLength) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    
    try {
      const response = await searchEscoSkills(query, type, maxSuggestions);
      const limitedResults = response.results.slice(0, maxSuggestions);
      setSuggestions(limitedResults);
      setShowSuggestions(limitedResults.length > 0);
      setSelectedIndex(-1);
    } catch (error) {
      console.error("Error searching skills:", error);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle input change with debouncing
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange({ skill: newValue });

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search by 300ms
    searchTimeoutRef.current = setTimeout(() => {
      searchSkills(newValue);
    }, 300);
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (skill) => {
    onChange({ skill: skill });
    onSelect?.(skill);
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    // Call parent onKeyDown if provided
    onKeyDown && onKeyDown(e);
    
    if (!showSuggestions || suggestions.length === 0) {
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case "Enter":
        if (selectedIndex >= 0) {
          e.preventDefault();
          handleSuggestionSelect(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  // Handle input focus
  const handleFocus = () => {
    const inputValue = value || "";
    if (inputValue.length >= minQueryLength && suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        !inputRef.current?.contains(event.target)
      ) {
        setShowSuggestions(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className={`skill-autocomplete ${className}`} style={{ position: 'relative' }}>
      <div className="skill-autocomplete__input-container">
        <input
          ref={inputRef}
          type="text"
          value={value || ""}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholder}
          className={`skill-autocomplete__input ${isSelected ? 'skill-selected' : ''}`}
          autoComplete="off"
        />
        
        {loading && (
          <div className="skill-autocomplete__loading">
            <FontAwesomeIcon icon={faSpinner} spin />
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsRef}
          className="skill-autocomplete__suggestions"
        >
          {suggestions.map((skillObject, index) => (
            <div
              key={`${index}-${skillObject.id}`}
              className={`skill-autocomplete__suggestion ${
                index === selectedIndex ? 'skill-autocomplete__suggestion--selected' : ''
              }`}
              onClick={() => handleSuggestionSelect(skillObject)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <span className="skill-autocomplete__suggestion-text">{skillObject.skill}</span>
              <FontAwesomeIcon 
                icon={faCheck} 
                className="skill-autocomplete__suggestion-icon"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SkillAutocomplete;
